import {computed, signal} from '@angular/core'
import {LoanProductMap} from '../0-application/shared'
import {DummyConfiguration, IConfiguration} from '../services/configuration'
import {DummyCalculus, ICalculus} from '../services/calculus/calculus'
import {Base, ICollectionBase} from './base'
import {ILoan, ILoanSummary, ISummarizeLoan, TCollectionType} from '../0-application/types'

export class Loan extends Base<ILoan & ICollectionBase> implements ISummarizeLoan {
  public override collection = 'loans' as TCollectionType

  public accountNo: string
  public amortization: number
  public start: number
  public isOwner: boolean
  // The interest before discount is set by server
  public profile: number
  // The owner percent not of interest
  public percent: number

  public readonly binding = signal<number>(0)
  public readonly discount = signal<number>(0)
  public readonly fixEnd = signal<number>(0)
  public readonly fixedInterest = signal<number>(0)
  public readonly productId = signal<string>('')
  public readonly productName = computed(() => LoanProductMap.get(this.productId()))
  public readonly amount = signal<number>(0)

  public configuration = signal<IConfiguration>(new DummyConfiguration())
  public calculus = signal<ICalculus>(new DummyCalculus())

  public interest = computed(() => {
    if (this.fixedInterest() !== 0) {
      return this.fixedInterest()
    }
    return this.listInterest() - this.discount()
  })

  public listInterest = computed(() =>
    this.configuration().listInterest(this.productId(), this.binding()))

  /**
   * Ltv should be calculated differently for old vs new loans?
   */
  public ltv = computed(() => this.calculus().ltvNew())

  public internalInterest = computed(() => {
    return this.configuration().internalInterest(this.productId(), this.binding(), this.ltv() * 100)
  })

  /**
   * The difference between actual interest and interest if internal
   * interest is used.
   */
  public profit = computed(() => {
    return this.amount() * this.interest() / 100 - this.amount() * this.internalInterest() / 100
  })

  /**
   * A loan may have an optional "campaign"
   */
  public campaign = signal<string | null>(null)

  constructor(loan: ILoan) {
    super(loan as any, loan.owner)
    /**
     * The following fields are not editable at the moment.
     */
    this.accountNo = loan.accountNo
    this.amortization = loan.amortization
    this.start = loan.start
    this.owner = loan.owner
    this.isOwner = loan.isOwner
    this.profile = loan.profile
    this.percent = loan.percent
    this.binding.set(loan.binding)
    this.discount.set(Math.abs(loan.discount)) // We want discounts as positive numbers.
    this.fixEnd.set(loan.fixEnd)
    this.fixedInterest.set(loan.isNew ? 0 : loan.interest)
    this.productId.set(loan.productId)
    this.amount.set(loan.amount)
    this.campaign.set(loan.campaign)
  }

  public override toPlainObject(): ILoan & ICollectionBase {
    const base = super.toPlainObject()
    return {
      ...base,
      accountNo: this.accountNo,
      amortization: this.amortization,
      start: this.start,
      owner: this.owner,
      isOwner: this.isOwner,
      profile: this.profile,
      percent: this.percent,
      binding: this.binding(),
      discount: this.discount(),
      fixEnd: this.fixEnd(),
      interest: this.interest(),
      productId: this.productId(),
      amount: this.amount(),
      campaign: this.campaign()
    }
  }

  public summarize(): ILoanSummary {
    return {
      productId: this.productId(),
      productName: this.productName()!,
      amount: this.amount(),
      interest: this.interest(),
      discount: this.discount(),
      campaign: this.campaign() ?? ''
    }
  }
}
